import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as endpoints from "@/api/endpoints";
import type { CartResponse } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";

interface CartContextValue {
  cart: CartResponse;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setCart: (c: CartResponse) => void;
}

const EMPTY: CartResponse = { items: [], total_amount: 0, item_count: 0 };

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [cart, setCart] = useState<CartResponse>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.fetchCart();
      setCart(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "カート取得に失敗しました");
      setCart(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      void refresh();
    } else if (status === "unauthenticated") {
      setCart(EMPTY);
    }
  }, [status, refresh]);

  const value = useMemo<CartContextValue>(
    () => ({ cart, loading, error, refresh, setCart }),
    [cart, loading, error, refresh],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
